/* eslint-disable no-undef */
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.scss';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import App from './App';
import { Provider as ErrorProvider, ErrorBoundary } from '@rollbar/react';
import { PostHogProvider } from 'posthog-js/react';
import type { PostHogConfig } from 'posthog-js';
import { sanitizePath } from './analytics/sanitizePath';
import { FeatureFlagProvider } from './context/FeatureFlagsProvider';
import { AuroraProvider } from './aurora/theme/AuroraProvider';
import { store } from './state/store';
import { Provider as StateProvider } from 'react-redux';

const rollbarConfig =
  process.env.REACT_APP_ROLLBAR_ACCESS_TOKEN &&
  process.env.REACT_APP_ENVIRONMENT
    ? {
        accessToken: process.env.REACT_APP_ROLLBAR_ACCESS_TOKEN,
        environment: process.env.REACT_APP_ENVIRONMENT,
      }
    : {
        enabled: false,
      };

const posthogKey = process.env.REACT_APP_POSTHOG_KEY;
const posthogEnv = process.env.REACT_APP_ENVIRONMENT;
const captureEnabled = posthogEnv === 'production' || posthogEnv === 'staging';

const sanitizeUrl = (url: unknown): unknown => {
  if (typeof url !== 'string') return url;
  try {
    const parsed = new URL(url);
    return parsed.origin + sanitizePath(parsed.pathname);
  } catch {
    return sanitizePath(url);
  }
};

const URL_PROP_KEYS = [
  '$current_url',
  '$initial_current_url',
  '$referrer',
  '$initial_referrer',
];
const PATH_PROP_KEYS = ['$pathname', '$initial_pathname'];

const sanitizePropertyBag = (bag: Record<string, unknown> | undefined) => {
  if (!bag) return bag;
  const out = { ...bag };
  URL_PROP_KEYS.forEach((key) => {
    if (out[key] != null) out[key] = sanitizeUrl(out[key]);
  });
  PATH_PROP_KEYS.forEach((key) => {
    if (typeof out[key] === 'string')
      out[key] = sanitizePath(out[key] as string);
  });
  return out;
};

const posthogOptions: Partial<PostHogConfig> = {
  api_host: `${window.location.origin}/ingest`,
  ui_host: 'https://us.posthog.com',
  capture_pageview: false,
  capture_pageleave: true,
  autocapture: captureEnabled,
  mask_all_text: true,
  mask_all_element_attributes: true,
  session_recording: {
    maskAllInputs: true,
    captureCanvas: {
      recordCanvas: true,
      canvasFps: 3,
      canvasQuality: '0.6',
    },
  },
  disable_session_recording: !captureEnabled,
  sanitize_properties: (properties) => {
    const sanitized = sanitizePropertyBag(properties) as typeof properties;
    if (sanitized.$set) sanitized.$set = sanitizePropertyBag(sanitized.$set);
    if (sanitized.$set_once)
      sanitized.$set_once = sanitizePropertyBag(sanitized.$set_once);
    return sanitized;
  },
  loaded: (posthog) => {
    if (posthogEnv) posthog.register({ environment: posthogEnv });
    if (captureEnabled) posthog.opt_in_capturing();
    else posthog.opt_out_capturing();
  },
};

// eslint-disable-next-line no-undef
if (process.env.NODE_ENV === 'production') {
  if (typeof window.__REACT_DEVTOOLS_GLOBAL_HOOK__ === 'object') {
    for (const [key, value] of Object.entries(
      window.__REACT_DEVTOOLS_GLOBAL_HOOK__
    )) {
      window.__REACT_DEVTOOLS_GLOBAL_HOOK__[key] =
        typeof value == 'function' ? () => {} : null;
    }
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Failed to find the root element');
}

const tree = (
  // <React.StrictMode>
  <BrowserRouter>
    <FeatureFlagProvider>
      <AuroraProvider>
        <StateProvider store={store}>
          <ErrorProvider config={rollbarConfig}>
            <ErrorBoundary>
              <Routes>
                <Route path="/*" element={<App />} />
              </Routes>
            </ErrorBoundary>
          </ErrorProvider>
        </StateProvider>
      </AuroraProvider>
    </FeatureFlagProvider>
  </BrowserRouter>
  // </React.StrictMode>
);

const root = ReactDOM.createRoot(rootElement);
root.render(
  // Only mount the PostHog provider when a key is configured; otherwise the
  // app renders unchanged and no analytics SDK is loaded.
  posthogKey ? (
    <PostHogProvider apiKey={posthogKey} options={posthogOptions}>
      {tree}
    </PostHogProvider>
  ) : (
    tree
  )
);
